import { useEffect, useState } from 'react';

import {
  getAccountId,
  getBillingCustomerCurrency,
  getCountryCode,
  getIsSuspended,
  getIsTrialer,
} from '../../../api/DistUI';
import { getStripeCustomer, getStripeCustomerPortalDetails } from '../../../api/StripeApi';
import { BillingSvcApi, ContactSvc, MyAccountApi } from '../api';
import { useUpgradeContext } from '../UpgradeContext';
import { isUnknown } from '../utils/isUnknown';
import { normalizeUpgradePromotion } from '../utils/normalizeUpgradePromotion';

const { getRecommendedUpgradePlanPromotion } = BillingSvcApi;
const { getContactsCount } = ContactSvc;
const { getRecommendedUpgradePlan, getBillingInfo, getCustomerInfo, getBillingAccount } = MyAccountApi;

const useInitalApiCalls = ({ usage, featureRequirements, feature, activity, redirectToBillingHome, promoCode }) => {
  const [isLoaded, setIsLoaded] = useState(false);

  const [upgradeContext, dispatchUpgrade] = useUpgradeContext();

  useEffect(() => {
    const IGNORE_ERROR = 'IGNORE_ERROR';

    async function initTrialerOrSuspended() {
      const [isTrialer, isSuspended] = await Promise.all([getIsTrialer(), getIsSuspended()]);

      dispatchUpgrade({ type: 'setUpgradeIsTrialer', value: isTrialer });
      dispatchUpgrade({ type: 'setUpgradeIsSuspended', value: isSuspended });

      if (isSuspended || isTrialer) throw new Error(IGNORE_ERROR);
    }

    async function initUpgradeInfo() {
      try {
        const { data: recommendedUpgradePlanPromotionLocal } = await getRecommendedUpgradePlanPromotion(promoCode);

        dispatchUpgrade({
          type: 'setRecommendedUpgradePlanPromotion',
          value: recommendedUpgradePlanPromotionLocal || false,
        });

        if (recommendedUpgradePlanPromotionLocal) {
          // Normalize field names before dispatching — the promotion response shape
          // differs between upgrade paths (legacy→V21 vs Micro Email→3PL).
          dispatchUpgrade({
            type: 'setUpgradeInfo',
            value: normalizeUpgradePromotion(recommendedUpgradePlanPromotionLocal),
          });

          return;
        }
      } catch {
        // just keep going with classic experience
        dispatchUpgrade({
          type: 'setRecommendedUpgradePlanPromotion',
          value: false,
        });
      }

      if (isUnknown(feature)) {
        redirectToBillingHome();
      }

      const countryCode = await getCountryCode();
      let promises = [];

      if (featureRequirements?.length) {
        promises = featureRequirements.map(({ feature: mapFeature, activity: mapActivity, usage: mapUsage }) => {
          return getRecommendedUpgradePlan({
            feature: mapFeature,
            activity: mapActivity,
            usage: mapUsage,
            countryCode,
          });
        });
      }

      if (feature && activity) {
        promises.push(
          getRecommendedUpgradePlan({
            feature,
            activity,
            usage,
            countryCode,
          })
        );
      }

      // Make all calls to getRecommendedUpgradePlan in parallel
      const recommendedPlans = await Promise.all(promises); // here we get the plans: current one and recommended one.

      const order = { BRONZE: 1, SILVER: 2, GOLD: 3 };

      const sortedPlans = recommendedPlans.sort((a, b) => {
        return order[a.recommendedPlanType] - order[b.recommendedPlanType];
      });

      // use the last, highest recommended plan
      const upgradePlanInfoLocal = sortedPlans[sortedPlans.length - 1];

      dispatchUpgrade({
        type: 'setUpgradeInfo',
        value: upgradePlanInfoLocal,
      });
    }

    async function getIsStripeAccount() {
      const billingAccount = await getBillingAccount();

      return billingAccount.billingSystemVersion >= 4;
    }

    async function initSecondaryData({ isStripeAccount }) {
      const [customerCurrencyCode, { contacts_count: contactCount }, billingInfo, customerId, stripeCustomer] =
        await Promise.all([
          (async () => {
            if (isStripeAccount) {
              const stripeCustomer = await getStripeCustomer();

              const paymentToken = stripeCustomer?.paymentMethod?.id;

              if (paymentToken) {
                dispatchUpgrade({ type: 'setStripePaymentToken', value: paymentToken });
              }

              return stripeCustomer.customer?.currency;
            }

            return getBillingCustomerCurrency();
          })(),
          getContactsCount(),
          getBillingInfo(),
          getAccountId(),
          (async () => {
            try {
              return await getStripeCustomer();
            } catch (e) {
              // Return null instead of throwing - this is expected for non-Stripe accounts
              return null;
            }
          })(),
        ]);

      const stripeToken = stripeCustomer?.paymentMethod?.id;

      dispatchUpgrade({ type: 'setUpgradeCurrency', value: customerCurrencyCode.toUpperCase() });
      dispatchUpgrade({ type: 'setUpgradeContactCount', value: contactCount });
      dispatchUpgrade({ type: 'setContactTiers', contactTiers: billingInfo?.overview?.contact_tier });
      dispatchUpgrade({ type: 'setUpgradeBillingInfo', value: billingInfo });

      dispatchUpgrade({ type: 'setIsStripeAccount', value: isStripeAccount });

      if (isStripeAccount) {
        const { upgradeReturnURL } = upgradeContext;

        const stripePortalDetails = await getStripeCustomerPortalDetails({
          flowType: 'payment_method_update',
          returnUrl: upgradeReturnURL || document.location.href,
        });

        dispatchUpgrade({ type: 'setStripePortalURL', value: stripePortalDetails.url });
      }

      if (stripeToken) {
        dispatchUpgrade({ type: 'setStripeToken', value: stripeToken });
      }

      const {
        customer_info: customerInfo,
        overview: { next_charge_scheduled: nextChargeScheduled },
      } = billingInfo;

      const formattedCustomerInfo = getCustomerInfo({
        customerInfo,
        currency: customerCurrencyCode.toUpperCase(),
        customerId,
      });

      dispatchUpgrade({ type: 'setUpgradeFormattedCustomerInfo', value: formattedCustomerInfo });

      const nextChargeScheduledDate = new Date(nextChargeScheduled);

      nextChargeScheduledDate.setDate(nextChargeScheduledDate.getDate() - 1);

      const dateStringOptions = { month: 'long', day: 'numeric' };

      if (nextChargeScheduledDate.getFullYear() !== new Date().getFullYear()) {
        dateStringOptions.year = 'numeric';
      }

      const nextChargeScheduledDateDisplayString = nextChargeScheduledDate.toLocaleString('default', dateStringOptions);

      dispatchUpgrade({
        type: 'setNextChargeScheduledDateDisplayString',
        value: nextChargeScheduledDateDisplayString,
      });
    }

    async function main() {
      try {
        const isStripeAccount = await getIsStripeAccount();

        dispatchUpgrade({ type: 'setIsStripeAccount', value: isStripeAccount });

        await initTrialerOrSuspended();

        await initUpgradeInfo();

        await initSecondaryData({ isStripeAccount });
      } catch (e) {
        if (e.message === IGNORE_ERROR) {
          return;
        }

        // eslint-disable-next-line no-console
        console.error(e);
      } finally {
        setIsLoaded(true);
      }
    }

    main();
  }, [usage, featureRequirements, feature, activity, redirectToBillingHome, promoCode]);

  return { isLoaded };
};

export default useInitalApiCalls;
